import React from 'react';
import PropTypes from 'prop-types';
import { Link } from '@resy/ui-kitchen';
import { getRetinaImage, handleMktAnalytics } from '../../AmexMarketingUtils/AmexUtils';
import { sanitizeString } from '../../../../../../ui-kitchen/src/utils/sanitizeString';
import { CTA_EVENT_NAME } from '../../AmexMarketingUtils/constants';
import './AmexResyCreditCardSpecific.scss';

const AmexResyCreditCardSpecific = (
  {
    awsFolderName,
    baseRequestUrl,
    cardsConfig: {
      cards = [],
    },
    cardName = '',
    matchMedia,
    eventAnalytics,
  }
) => {
  if (cards.length === 0) return null;
  const activeCard = cards.find((item) => item.type === cardName);
  if (!activeCard) return null;

  const displayCTA = (cta) => {
    const {
      label,
      url,
      aria_label: ariaLabel,
    } = cta;
    if (!label || !url) return false;

    return (
      <div>
        <Link
          className="AmexResyCreditCardSpecific__CTA"
          href={url}
          key={label}
          target="_blank"
          onClick={() => {
            handleMktAnalytics(eventAnalytics, CTA_EVENT_NAME, {
              action_name: `${label} Click`,
            });
          }}
          aria-label={ariaLabel}
        >
          { label }
        </Link>
      </div>
    );
  };

  const renderCardContent = () => {
    const {
      id: sectionId,
      title: sectionTitle = '',
      tagging_title: taggingTitle,
      subtitle: sectionSubtitle = '',
      card_title: cardTitle = '',
      card_subtitle: cardSubtitle = '',
      card_copy: cardCopy = '',
      image: {
        desktop: desktopImg = {},
        mobile: mobileImg = {},
      } = {},
      cta = {},
    } = activeCard;

    const isMobile = !matchMedia.lg;
    const imageName = getRetinaImage(desktopImg, mobileImg, matchMedia.lg) || null;
    const urlPrefix = `${baseRequestUrl}${awsFolderName}/carouselImages/tabBgImages`;
    const imageSrc = `${urlPrefix}/${imageName}`;
    const style = {
      backgroundImage: !isMobile && `url(${imageSrc})`,
    };

    const sanitizedSectionTitle = sanitizeString(sectionTitle);
    const sanitizedSectionSubtitle = sanitizeString(sectionSubtitle);
    const sanitizedCardTitle = sanitizeString(cardTitle);
    const sanitizedCardSubtitle = sanitizeString(cardSubtitle);
    const sanitizedCardCopy = sanitizeString(cardCopy);

    return (
      <>
        {sectionTitle && (
          <h2
            id={sectionId}
            className="AmexResyCreditCardSpecific__section-title"
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{
              __html: sanitizedSectionTitle,
            }}
          />
        )}
        {sectionSubtitle && (
          <h3
            className="AmexResyCreditCardSpecific__section-subtitle"
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{
              __html: sanitizedSectionSubtitle,
            }}
          />
        )}
        <div
          className="AmexResyCreditCardSpecific__card"
          {...{ style }}
        >
          <div className="AmexResyCreditCardSpecific__card--content">
            {cardTitle && (
              <h4
                className="AmexResyCreditCardSpecific__card--title"
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{
                  __html: sanitizedCardTitle,
                }}
              />
            )}
            {cardSubtitle && (
              <h5
                className="AmexResyCreditCardSpecific__card--subtitle"
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{
                  __html: sanitizedCardSubtitle,
                }}
              />
            )}
            {cardCopy && (
              <div
                className="AmexResyCreditCardSpecific__card--copy"
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{
                  __html: sanitizedCardCopy,
                }}
              />
            )}
            {Object.keys(cta).length > 0 && displayCTA(cta, taggingTitle)}
            {(isMobile && imageName) && (
              <img
                className="AmexResyCreditCardSpecific__card--img"
                src={imageSrc}
                alt=""
              />
            )}
          </div>
        </div>
      </>
    );
  };

  return (
    <section className="AmexResyCreditCardSpecific vertical-height">
      <div className="container">
        {renderCardContent()}
      </div>
    </section>
  );
};

AmexResyCreditCardSpecific.propTypes = {
  awsFolderName: PropTypes.string.isRequired,
  baseRequestUrl: PropTypes.string.isRequired,
  cardsConfig: PropTypes.shape({
    cards: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    title: PropTypes.string.isRequired,
    section_id: PropTypes.string.isRequired,
    event_tag: PropTypes.string.isRequired,
  }).isRequired,
  cardName: PropTypes.string.isRequired,
  matchMedia: PropTypes.shape({
    xs: PropTypes.bool,
    sm: PropTypes.bool,
    md: PropTypes.bool,
    lg: PropTypes.bool,
    xl: PropTypes.bool,
  }).isRequired,
  eventAnalytics: PropTypes.func.isRequired,
};

export default AmexResyCreditCardSpecific;
